import React from "react";
import RowContainer from "../Common/RowContainer";
import Particles from "../Common/ParticleDesign";
import { removeInlineColors } from "../../utils/htmlColorUtils";

interface FlashCardComponentData {
  lightTheme?: string | null;
  britzelId?: number | null;
  header?: string | null;
  flashCardContentComponentId?: number | null;
  sentences?: {
    value: string;
    particleValue1?: string | null;
    type: string;
    particleType1?: string | null;
  }[];
  footer?: string | null;
  size?: string | null;
}

interface FlashcardComponent {
  id?: number;
  contentComponentType: string;
  flashCardContentComponentId?: number;
  data?: FlashCardComponentData;
}

interface ExampleComponentProps {
  flashCardComponentData: FlashCardComponentData;
  isHalf?: boolean;
  isLast?: boolean;
  isBretzel?: boolean;
  currentFlashcard?: { components?: FlashcardComponent[] };
  currentFlashCardComponentIndex: number;
  onClick: (id?: number) => void;
}

const ExampleComponent: React.FC<ExampleComponentProps> = ({
  flashCardComponentData,
  isHalf = false,
  currentFlashcard,
  currentFlashCardComponentIndex,
  onClick,
}) => {
  /**
   * Flutter logic:
   *   lightTheme == "Light" || britzelId != null → bg #110929, text white
   *   else (dark, no bretzel)                    → bg transparent, text #16162F
   */
  const isLight = flashCardComponentData.lightTheme === "Light";
  const hasBretzel = flashCardComponentData.britzelId != null;

  const bgColor = isLight || hasBretzel ? "#110929" : "transparent";
  const textColor = isLight || hasBretzel ? "#FFFFFF" : "#16162F";
  const headerFooterColor = isLight || hasBretzel
    ? "rgba(255,255,255,0.75)"
    : "rgba(22,22,47,0.75)";

  const sizefullExample = () => (
    <RowContainer
      bgColor={bgColor}
      padding="8px 11px"
      borderRadius="24px"
      gap="6px"
      flexDirection="column"
      justifyContent="center"
      cursor="pointer"
      onClick={() => {
        if (hasBretzel) {
          const compId = currentFlashcard?.components?.[currentFlashCardComponentIndex]
            .flashCardContentComponentId;
          if (compId) onClick(compId);
        } else {
          if (flashCardComponentData.flashCardContentComponentId) {
            onClick(currentFlashcard?.components?.[currentFlashCardComponentIndex].id);
          }
        }
      }}
    >
      {flashCardComponentData.header && (
        <div
          dangerouslySetInnerHTML={{
            __html: removeInlineColors((flashCardComponentData.header || "").cleanUpString()),
          }}
          style={{ width: "100%", color: headerFooterColor }}
        />
      )}

      <RowContainer
        alignItem="center"
        justifyContent="center"
        gap="4px"
        flexWrap="wrap"
        color={textColor}
      >
        {flashCardComponentData.sentences?.map((sentence, index) => (
          <Particles
            key={index}
            text={[sentence.value ?? "", sentence.particleValue1 ?? ""]}
            type={[sentence.type ?? "", sentence.particleType1 ?? ""]}
            textColor={textColor}
          />
        ))}
      </RowContainer>

      {flashCardComponentData.footer && (
        <div
          dangerouslySetInnerHTML={{
            __html: removeInlineColors((flashCardComponentData.footer || "").cleanUpString()),
          }}
          style={{ width: "100%", color: headerFooterColor }}
        />
      )}
    </RowContainer>
  );

  const renderGrid = () => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "16px",
        padding: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      {getConsecutiveComponents(currentFlashcard?.components, currentFlashCardComponentIndex)
        .map((component, index) => (
          <ExampleComponent
            key={index}
            flashCardComponentData={component.data || {}}
            isHalf={false}
            currentFlashCardComponentIndex={currentFlashCardComponentIndex + index}
            currentFlashcard={currentFlashcard}
            isBretzel={!!component?.data?.britzelId}
            onClick={() => {
              const componentId = component?.data?.britzelId
                ? component.flashCardContentComponentId
                : component.id;
              if (componentId) onClick(componentId);
            }}
          />
        ))}
    </div>
  );

  const getConsecutiveComponents = (
    components: FlashcardComponent[] | undefined,
    startIndex: number
  ): FlashcardComponent[] => {
    const result: FlashcardComponent[] = [];
    if (!components || startIndex >= components.length) return result;
    for (let i = startIndex; i < components.length; i++) {
      const el = components[i];
      if (el.contentComponentType === "example" && el.data?.size === "Half") {
        result.push(el);
      } else break;
    }
    return result;
  };

  if (currentFlashCardComponentIndex > 0 && isHalf) {
    const prev = currentFlashcard?.components?.[currentFlashCardComponentIndex - 1];
    const next = currentFlashcard?.components?.[currentFlashCardComponentIndex + 1];
    if (currentFlashCardComponentIndex === (currentFlashcard?.components?.length ?? 0) - 1) return <div />;
    if (next?.contentComponentType === "example" && next?.data?.size === "Full") return <div />;
    if (prev?.contentComponentType === "example" && prev?.data?.size === "Half" && next?.data?.size === "Half") return <div />;
  }

  return isHalf ? renderGrid() : sizefullExample();
};

export default ExampleComponent;
